<template>
  <div class="page">
    <van-nav-bar title="内容创作台" left-arrow @click-left="$router.back()"/>
    <section v-if="requirement" class="direction"><b>{{ field(requirement, 'title') }}</b>
      <p>{{ field(requirement, 'direction_text') }}</p></section>
    <van-form class="create-form" @submit="saveDraft">
      <van-field v-model="form.title" label="主题" placeholder="这次要创作什么" required/>
      <van-field v-model="form.platform" label="平台" placeholder="抖音、视频号、公众号等"/>
      <van-field :value="contentTypeLabel" label="形式" readonly is-link @click="showType=true"/>
      <van-field v-model="form.materialText" type="textarea" rows="3" label="素材" placeholder="粘贴事实、创意、产品资料或文案初稿" required/>
      <div class="uploader">
        <div class="uploader-head"><label>图片/文件素材</label><span>最多 6 个</span></div>
        <van-uploader :after-read="uploadMaterial" :max-count="6" multiple/>
      </div>
      <van-field v-model="tagText" label="标签" placeholder="多个标签用逗号分隔"/>
      <van-field v-model="form.shootNote" type="textarea" rows="2" label="实拍登记" placeholder="拍摄时间、场地、人员或素材链接"/>
      <div class="actions">
        <van-button block type="primary" native-type="submit" :loading="saving">保存草稿</van-button>
      </div>
    </van-form>
    <van-popup v-model="showType" position="bottom">
      <van-picker show-toolbar value-key="label" :columns="types" @confirm="selectType" @cancel="showType=false"/>
    </van-popup>

    <section v-if="current" class="panel">
      <h2>视频智能分析</h2>
      <van-field v-model="videoContext.script" type="textarea" rows="2" label="视频脚本" placeholder="粘贴计划脚本"/>
      <van-field v-model="videoContext.subtitles" type="textarea" rows="2" label="字幕文本" placeholder="粘贴字幕或字幕文件内容"/>
      <van-field v-model="videoContext.transcript" type="textarea" rows="2" label="语音转写" placeholder="粘贴 ASR 转写结果；未提供时不臆测音频"/>
      <p class="metrics-tip">分析画面、脚本、字幕与语音转写的一致性。未提供语音转写时，只分析视觉内容。</p>
      <van-button block plain type="primary" :disabled="!videoMaterial" :loading="analyzingVideo" @click="analyzeVideo">分析已上传视频</van-button>
      <div class="panel-actions">
        <van-button block type="primary" :disabled="!videoMaterial" :loading="analyzingVideo" loading-text="正在分析视频" @click="analyzeVideo">
          {{ videoMaterial ? '分析已上传视频' : '请先上传视频素材' }}
        </van-button>
      </div>
      <div v-if="videoResult" class="result"><p><b>综合评分：</b>{{ field(videoResult, 'overallScore') || 0 }}</p>
        <pre>{{ field(videoResult, 'summary') }}</pre>
        <p v-if="field(videoResult,'recommendations')"><b>优化动作：</b>{{ field(videoResult, 'recommendations') }}</p></div>
    </section>

    <section v-if="current" class="panel">
      <h2>AI 生成与评测</h2>
      <van-button block plain type="primary" :loading="generating" @click="generate">生成文案/脚本并评分</van-button>
      <van-button block plain type="danger" :loading="reviewing" @click="reviewCompliance">发布前合规质检</van-button>
      <van-button block plain type="primary" :loading="predicting" @click="predict">预测平台表现</van-button>
      <van-button block plain type="primary" :loading="comparing" :disabled="!prediction" @click="compare">对比实际表现</van-button>
      <van-button block plain type="warning" :loading="nominating" @click="nominate('EXCELLENT')">提名优秀样本</van-button>
      <van-button block plain type="danger" :loading="nominating" @click="nominate('FAILED')">提名失败样本</van-button>
      <div class="workflow">
        <div class="workflow-group">
          <div class="workflow-head"><span class="step-index">1</span>
            <div><b>创作与质检</b><small>先生成内容，再检查品牌与宣传合规</small></div>
          </div>
          <div class="action-grid">
            <van-button block type="primary" :loading="generating" loading-text="正在生成内容" @click="generate">生成文案/脚本</van-button>
            <van-button block plain type="primary" :loading="reviewing" loading-text="正在质检" @click="reviewCompliance">发布前合规质检</van-button>
          </div>
        </div>
        <div class="workflow-group">
          <div class="workflow-head"><span class="step-index">2</span>
            <div><b>效果验证</b><small>发布前预测，发布后登记数据进行对比</small></div>
          </div>
          <div class="action-grid">
            <van-button block plain type="primary" :loading="predicting" loading-text="正在预测" @click="predict">预测平台表现</van-button>
            <van-button block plain type="primary" :loading="comparing" loading-text="正在对比" :disabled="!prediction" @click="compare">
              {{ prediction ? '对比实际表现' : '预测后可对比' }}
            </van-button>
          </div>
        </div>
        <div class="workflow-group workflow-group--last">
          <div class="workflow-head"><span class="step-index">3</span>
            <div><b>沉淀内容样本</b><small>由运营审核后进入下一轮品牌策划上下文</small></div>
          </div>
          <div class="sample-actions">
            <van-button block plain :loading="nominating" loading-text="正在提交" @click="nominate('EXCELLENT')">提名优秀样本</van-button>
            <van-button block plain :loading="nominating" loading-text="正在提交" @click="nominate('FAILED')">提名失败样本</van-button>
          </div>
        </div>
      </div>
      <div v-if="compliance" class="result"><p><b>发布门禁：</b>{{ field(compliance, 'gateResult') }}</p>
        <p><b>风险等级：</b>{{ field(compliance, 'riskLevel') }}</p></div>
      <div v-if="prediction" class="result"><p><b>预测分：</b>{{ field(prediction, 'predictedScore') || 0 }}</p>
        <p><b>置信度：</b>{{ field(prediction, 'confidence') || 0 }}</p></div>
      <div v-if="generated" class="result">
        <pre>{{ generated }}</pre>
        <p v-if="optimization"><b>优化建议：</b>{{ optimization }}</p></div>
      <van-field v-model.number="metrics.manualScore" type="number" label="人工评分" placeholder="0-100"/>
      <van-field v-model.number="metrics.viewCount" type="digit" label="阅读/播放"/>
      <van-field v-model.number="metrics.likeCount" type="digit" label="点赞"/>
      <van-field v-model.number="metrics.commentCount" type="digit" label="评论"/>
      <van-field v-model.number="metrics.shareCount" type="digit" label="分享/转发"/>
      <van-field v-model.number="metrics.conversionCount" type="digit" label="转化"/>
      <van-field v-model.number="outcome.projectId" type="digit" label="关联项目ID" placeholder="可选"/>
      <van-field v-model.trim="outcome.campaignName" label="活动名称" placeholder="本次内容所属活动"/>
      <van-field v-model.number="outcome.productionCost" type="number" label="制作成本" placeholder="元"/>
      <van-field v-model.number="outcome.leadCount" type="digit" label="有效线索"/>
      <van-field v-model.number="outcome.dealCount" type="digit" label="成交数量"/>
      <van-field v-model.number="outcome.dealAmount" type="number" label="成交金额" placeholder="元"/>
      <van-field v-model.trim="metrics.platformContentId" label="平台作品ID" placeholder="发布后填写作品ID"/>
      <van-field v-model.trim="metrics.platformContentUrl" label="作品链接" placeholder="粘贴平台作品链接"/>
      <p class="metrics-tip">平台权限开通前可先登记官方后台数据；绑定授权账号后可自动同步。</p>
      <div class="actions">
        <van-button block type="danger" :loading="publishing" @click="publish">发布登记</van-button>
      </div>
    </section>

    <section class="panel"><h2>我的内容</h2>
      <van-cell v-for="item in items" :key="field(item,'id')" :title="field(item,'title')"
                :label="field(item,'status')+' · '+field(item,'platform')"/>
    </section>
  </div>
</template>
<script>
import api from "@/api/brand";

export default {
  data() {
    return {
      form: {title: "", platform: "", contentType: "COPY", materialText: "", shootNote: "", materialUrls: []},
      tagText: "",
      types: [{label: "文案", value: "COPY"}, {label: "图片创意", value: "IMAGE_BRIEF"}, {
        label: "视频脚本",
        value: "VIDEO_SCRIPT"
      }, {label: "实拍记录", value: "SHOOT_RECORD"}],
      showType: false,
      saving: false,
      generating: false,
      publishing: false,
      reviewing: false,
      predicting: false,
      comparing: false,
      nominating: false,
      analyzingVideo: false,
      videoMaterial: null,
      videoResult: null,
      videoContext: {script: "", subtitles: "", transcript: "", asrProvider: "MANUAL"},
      compliance: null,
      prediction: null,
      current: null,
      items: [],
      requirements: [],
      outcome: {projectId: null, campaignName: "", productionCost: 0, leadCount: 0, dealCount: 0, dealAmount: 0},
      metrics: {
        manualScore: null,
        viewCount: 0,
        likeCount: 0,
        commentCount: 0,
        shareCount: 0,
        conversionCount: 0,
        platformContentId: "",
        platformContentUrl: ""
      }
    }
  },
  computed: {
    requirement() {
      return this.requirements[0] || null
    }, contentTypeLabel() {
      const item = this.types.find(v => v.value === this.form.contentType);
      return item ? item.label : "请选择"
    }, generated() {
      return this.field(this.current, "generated_content") || ""
    }, optimization() {
      return this.field(this.current, "optimization_text") || ""
    }
  },
  created() {
    this.reload()
  },
  methods: {
    field(row, key) {
      if (!row) return "";
      return row[key] !== undefined ? row[key] : row[key.toUpperCase()]
    }, async reload() {
      try {
        const [items, requirements] = await Promise.all([api.contents(false), api.requirements()]);
        this.items = items;
        this.requirements = requirements
      } catch (e) {
        this.$toast.fail(e.message)
      }
    }, selectType(v) {
      this.form.contentType = v.value;
      this.showType = false
    }, async uploadMaterial(item) {
      try {
        const result = await api.uploadMaterial(item.file);
        this.form.materialUrls.push(result.url);
        if ((item.file.type || '').indexOf('video/') === 0) this.videoMaterial = result;
        item.status = 'done';
        item.message = '上传成功'
      } catch (e) {
        item.status = 'failed';
        item.message = e.message;
        this.$toast.fail(e.message)
      }
    }, async analyzeVideo() {
      if (!this.current || !this.videoMaterial) return;
      this.analyzingVideo = true;
      try {
        const data = await api.analyzeVideo(this.field(this.current, 'id'), {...this.videoMaterial, ...this.videoContext});
        this.videoResult = data.result;
        this.$toast.success('视频分析完成')
      } catch (e) {
        this.$toast.fail(e.message)
      } finally {
        this.analyzingVideo = false
      }
    }, async saveDraft() {
      this.saving = true;
      try {
        this.current = await api.createContent({...this.form, tags: this.tagText.split(/[,，]/).map(v => v.trim()).filter(Boolean)});
        this.$toast.success("草稿已保存");
        await this.reload()
      } catch (e) {
        this.$toast.fail(e.message)
      } finally {
        this.saving = false
      }
    }, async generate() {
      this.generating = true;
      try {
        this.current = await api.generateEvaluate(this.field(this.current, "id"));
        this.$toast.success("生成与评测完成")
      } catch (e) {
        this.$toast.fail(e.message)
      } finally {
        this.generating = false
      }
    }, async reviewCompliance() {
      this.reviewing = true;
      try {
        this.compliance = await api.complianceReview(this.field(this.current, "id"));
        this.$toast.success(this.field(this.compliance, 'gateResult') === 'PASS' ? '合规质检通过' : '质检完成，请按要求修改')
      } catch (e) {
        this.$toast.fail(e.message)
      } finally {
        this.reviewing = false
      }
    }, async predict() {
      this.predicting = true;
      try {
        this.prediction = await api.predictContent(this.field(this.current, "id"));
        this.$toast.success('发布前预测已生成')
      } catch (e) {
        this.$toast.fail(e.message)
      } finally {
        this.predicting = false
      }
    }, async compare() {
      this.comparing = true;
      try {
        this.prediction = await api.comparePrediction(this.field(this.current, "id"));
        this.$toast.success('预测与实际表现已对比')
      } catch (e) {
        this.$toast.fail(e.message)
      } finally {
        this.comparing = false
      }
    }, async nominate(type) {
      this.nominating = true;
      try {
        await api.nominateSample(this.field(this.current, "id"), {sampleType: type, evidence: {metrics: this.metrics, outcome: this.outcome}});
        this.$toast.success('样本已提交运营审核')
      } catch (e) {
        this.$toast.fail(e.message)
      } finally {
        this.nominating = false
      }
    }, async publish() {
      const score = Number(this.metrics.manualScore);
      if (!Number.isFinite(score) || score < 0 || score > 100) {
        this.$toast("人工评分须为 0-100");
        return
      }
      if (this.field(this.compliance, 'gateResult') !== 'PASS') {
        this.$toast("请先完成并通过发布前合规质检");
        return
      }
      if (this.metrics.platformContentId && !this.form.platform.trim()) {
        this.$toast("请先填写发布平台");
        return
      }
      this.publishing = true;
      try {
        const id = this.field(this.current, "id");
        this.current = await api.publish(id, {publish: true, ...this.metrics});
        await api.saveOutcome(id, this.outcome);
        if (this.metrics.platformContentId) {
          await api.importPlatformMetrics(id, {
            platform: this.form.platform,
            platformContentId: this.metrics.platformContentId,
            platformContentUrl: this.metrics.platformContentUrl,
            viewCount: this.metrics.viewCount,
            likeCount: this.metrics.likeCount,
            commentCount: this.metrics.commentCount,
            shareCount: this.metrics.shareCount,
            conversionCount: this.metrics.conversionCount,
            sourceType: "MANUAL"
          })
        }
        this.$toast.success("发布及投效数据已登记");
        await this.reload()
      } catch (e) {
        this.$toast.fail(e.message)
      } finally {
        this.publishing = false
      }
    }
  }
};
</script>
<style lang="less" scoped>
@import "./brand-page.less";

.direction {
  padding: .28rem;
  border-left: .06rem solid #d32f2f
}

.direction b {
  font-size: .34rem
}

.direction p {
  margin: .12rem 0 0;
  color: #777b8e;
  font-size: .27rem;
  line-height: 1.65;
  white-space: pre-wrap
}

.create-form /deep/ .van-field--required::before {
  display: none
}

.create-form /deep/ .van-field--required .van-field__label::after {
  margin-left: .05rem;
  color: #ee5c5c;
  content: "*"
}

.create-form /deep/ .van-field__label {
  width: 1.65rem;
  flex: none
}

.create-form /deep/ textarea.van-field__control {
  min-height: 1.28rem;
  max-height: 2.2rem;
  line-height: 1.6;
  resize: none
}

.uploader {
  padding: .24rem .28rem .16rem;
  border-top: 1px solid #eceef3;
  background: #fff
}

.uploader-head {
  display: flex;
  margin-bottom: .18rem;
  align-items: center;
  justify-content: space-between
}

.uploader-head label {
  color: #5f6375;
  font-size: .31rem;
  line-height: .48rem
}

.uploader-head span {
  color: #a3a6b3;
  font-size: .24rem
}

.uploader /deep/ .van-uploader__upload, .uploader /deep/ .van-uploader__preview-image {
  width: 1.28rem;
  height: 1.28rem;
  margin: 0 .16rem .12rem 0;
  border-radius: .08rem;
  background: #f5f6f8
}

.uploader /deep/ .van-uploader__upload-icon {
  color: #c5c8d1;
  font-size: .42rem
}

.panel {
  padding-bottom: .08rem
}

.result {
  margin: .04rem .28rem .22rem;
  padding: .22rem .24rem;
  border-left: .05rem solid #d32f2f;
  border-radius: .08rem;
  background: #f8f8fa
}

.result pre {
  margin: 0;
  white-space: pre-wrap;
  font: inherit;
  font-size: .29rem;
  line-height: 1.7
}

.result p {
  margin: .16rem 0 0;
  color: #626678;
  font-size: .27rem;
  line-height: 1.6
}

.panel /deep/ .van-cell__title {
  font-weight: 600
}

.panel /deep/ .van-cell__label {
  margin-top: .08rem;
  color: #9295a7
}

.metrics-tip {
  margin: 0;
  padding: .16rem .28rem .24rem;
  color: #8b8e9e;
  font-size: .24rem;
  line-height: 1.5;
  background: #fff
}

.panel-actions {
  padding: .02rem .28rem .28rem
}

.workflow {
  padding: 0 .28rem .1rem
}

.workflow-group {
  padding: .22rem 0 .26rem;
  border-bottom: 1px solid #eceef3
}

.workflow-group--last {
  border-bottom: 0
}

.workflow-head {
  display: flex;
  align-items: flex-start;
  margin-bottom: .2rem
}

.workflow-head > div {
  min-width: 0
}

.workflow-head b {
  display: block;
  color: #34364a;
  font-size: .3rem;
  line-height: .42rem
}

.workflow-head small {
  display: block;
  margin-top: .04rem;
  color: #9295a7;
  font-size: .23rem;
  line-height: 1.45
}

.step-index {
  display: flex;
  width: .4rem;
  height: .4rem;
  margin-right: .14rem;
  flex: none;
  align-items: center;
  justify-content: center;
  border-radius: 50%;
  color: #fff;
  background: #d32f2f;
  font-size: .23rem;
  font-weight: 700
}

.action-grid, .sample-actions {
  display: grid;
  grid-template-columns:1fr 1fr;
  gap: .14rem
}

.workflow /deep/ .van-button--block {
  height: .76rem;
  font-size: .28rem
}

.workflow /deep/ .van-button--disabled {
  color: #a7a9b2;
  border-color: #e3e4e8;
  background: #f5f6f8;
  opacity: 1
}

.sample-actions /deep/ .van-button:first-child {
  color: #277a52;
  border-color: #b9dfcd;
  background: #f5fbf8
}

.sample-actions /deep/ .van-button:last-child {
  color: #b24949;
  border-color: #e8c2c2;
  background: #fff8f8
}

.panel-actions /deep/ .van-button--disabled {
  color: #999ca8;
  border-color: #e4e5e9;
  background: #f1f2f5;
  opacity: 1
}
</style>




// WEBPACK FOOTER //
// src/views/brand/CreateStudio.vue